import type { Schema } from '@repo/strapi'
import type React from 'react'
import { OptimizedImage } from '@/components/elementary/OptimizedImage'
import type { StrapiImageMedia } from '@/types/api'
import type { ImageExtendedProps } from '@/types/next'

export interface BasicImageProps extends Omit<ImageExtendedProps, 'src' | 'alt'> {
	readonly component?: Schema.Attribute.ComponentValue<'utilities.basic-image', false> | null
	readonly className?: ImageExtendedProps['className']
	readonly ignoreMediaSize?: boolean
}

export function BasicImage({
	component,
	className,
	ignoreMediaSize,
	...imgProps
}: BasicImageProps) {
	const media: StrapiImageMedia = component?.media

	if (!media?.url) {
		return null
	}

	const isFillLayout = 'fill' in imgProps && imgProps.fill

	const width = component?.width ?? imgProps?.width ?? (ignoreMediaSize ? undefined : media?.width)
	const height =
		component?.height ?? imgProps?.height ?? (ignoreMediaSize ? undefined : media?.height)

	const alt = media?.alternativeText ?? media?.name ?? ''

	const imageStyle: React.CSSProperties = {
		// When using fill, set absolute positioning and let parent handle dimensions
		...(isFillLayout
			? { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }
			: {
					// Convert numbers to pixel strings for explicit CSS units
					...(width !== undefined && { width: `${width}px`, maxWidth: '100%' }),
					...(height !== undefined && {
						maxHeight: `${height}px`,
						height: 'auto',
					}),
				}),
		...imgProps.style,
	}

	// Destructure fill out of imgProps so it doesn't get passed to OptimizedImage
	// biome-ignore lint/correctness/noUnusedVariables: fill is destructured to exclude from otherImgProps
	const { fill, ...otherImgProps } = imgProps

	const imageProps = {
		...otherImgProps,
		style: imageStyle,
		alt,
		// Add intrinsic width and height for layout stability (when not using fill)
		...(isFillLayout ? {} : { width: width, height: height }),
	}

	return (
		<OptimizedImage
			media={media}
			className={className}
			fallbackSrc={component?.fallbackSrc ?? undefined}
			{...imageProps}
		/>
	)
}
